import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TONE_CONFIG, CHANNEL_LABEL } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Send, Loader2, AlertTriangle, MessageSquare, Mail, Smartphone } from "lucide-react";

const TONES = ["SOFT", "STANDARD", "URGENT", "FINAL_REMINDER"];
const CHANNELS = [
  { key: "whatsapp", icon: MessageSquare },
  { key: "email", icon: Mail },
  { key: "sms", icon: Smartphone },
];

export default function ReminderPanel({ open, onClose, workerIds, onSent }) {
  const [tone, setTone] = useState("STANDARD");
  const [channel, setChannel] = useState("whatsapp");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !workerIds?.length) return;
    setLoading(true);
    api.post("/reminders/preview", { worker_ids: workerIds, tone, channel })
      .then((r) => setPreview(r.data))
      .catch(() => toast.error("No se pudo generar la vista previa"))
      .finally(() => setLoading(false));
  }, [open, workerIds, tone, channel]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/send", { worker_ids: workerIds, tone, channel });
      if (data.channel_configured) {
        toast.success(`Recordatorios enviados a ${data.sent} trabajadores`);
      } else {
        toast.warning(`${CHANNEL_LABEL[channel]} NO CONFIGURADO — se generaron ${data.not_configured} mensajes pero no se enviaron`);
      }
      onSent && onSent(data);
      onClose();
    } catch (e) {
      toast.error("No se pudo ejecutar el envío");
    } finally {
      setSending(false);
    }
  };

  const cost = preview?.estimated_cost;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-3xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b border-slate-100">
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Send size={18} className="text-blue-600" /> Enviar recordatorio a {workerIds?.length || 0} trabajadores
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">Cada mensaje se personaliza individualmente con el nombre y pendientes de cada trabajador.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 max-h-[70vh] overflow-y-auto">
          {/* Left: config */}
          <div className="p-6 space-y-5 border-r border-slate-100">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Tono del mensaje</p>
              <div className="grid grid-cols-2 gap-2">
                {TONES.map((t) => (
                  <button
                    key={t}
                    data-testid={`tone-selector-${t.toLowerCase().replace("_", "-")}`}
                    onClick={() => setTone(t)}
                    className={`text-left p-2.5 rounded-lg border transition-colors ${tone === t ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <p className={`text-sm font-semibold ${tone === t ? "text-blue-700" : "text-slate-700"}`}>{TONE_CONFIG[t].label}</p>
                    <p className="text-[11px] text-slate-400 leading-tight">{TONE_CONFIG[t].desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Canal</p>
              <div className="flex gap-2">
                {CHANNELS.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`channel-selector-${c.key}`}
                    onClick={() => setChannel(c.key)}
                    className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-lg border transition-colors ${channel === c.key ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  >
                    <c.icon size={18} /><span className="text-xs font-medium">{CHANNEL_LABEL[c.key]}</span>
                  </button>
                ))}
              </div>
            </div>

            {preview && !preview.channel_configured && (
              <div data-testid="channel-not-configured-warning" className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span><b>{CHANNEL_LABEL[channel]} NO CONFIGURADO.</b> Los mensajes se generarán de forma personalizada y quedarán registrados, pero no se enviarán hasta conectar el proveedor en Configuración.</span>
              </div>
            )}

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Costo estimado</p>
              {cost ? (
                <>
                  <p className="text-2xl font-display font-bold text-slate-800" data-testid="estimated-cost">
                    {cost.known ? `${cost.currency} ${cost.total.toFixed(2)}` : "N/D"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {cost.known ? `${cost.currency} ${cost.unit_price} x ${cost.count} mensajes · ${preview.provider}` : "Sin regla de precio para este canal"}
                  </p>
                </>
              ) : <p className="text-sm text-slate-400">—</p>}
            </div>
          </div>

          {/* Right: preview */}
          <div className="p-6 bg-slate-50/60">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              Vista previa personalizada
            </p>
            {loading ? (
              <div className="flex items-center justify-center h-40 text-slate-400"><Loader2 className="animate-spin" /></div>
            ) : (
              <div className="space-y-3">
                {preview?.samples?.map((s) => (
                  <div key={s.worker_id} className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
                    <p className="text-[11px] font-semibold text-blue-600 mb-1">{s.worker_name}</p>
                    <p className="text-[13px] text-slate-700 whitespace-pre-line leading-relaxed">{s.text}</p>
                  </div>
                ))}
                {preview && preview.count > (preview.samples?.length || 0) && (
                  <p className="text-xs text-slate-400 text-center">+ {preview.count - preview.samples.length} mensajes más, cada uno personalizado individualmente.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-800 px-4 py-2">Cancelar</button>
          <button
            data-testid="confirm-send-button"
            onClick={send}
            disabled={sending || loading || !preview?.count}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-sm"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Confirmar y enviar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
